import { FC } from 'react';
import type { AssignedAgent } from '../../types/contact.types';
import type { FormattedUser } from '../../hooks/graphql/users/useOrganizationUsers';
import { resolveAssignedAgent, formatAssignedAgentName } from '../../utils/assignedAgent';
import { formatPhoneNumber } from '../../utils/formatters';

interface AssignedAgentCellProps {
  /** Backend snapshot of the assigned agent (preferred when present). */
  agent?: AssignedAgent | null;
  /** Falls back to looking the agent up in `orgUsers` when no snapshot. */
  agentId?: string | null;
  /** Org users list — supplies the fallback lookup. */
  orgUsers?: FormattedUser[];
  /** Rendered when neither source resolves an agent. Defaults to "-". */
  emptyLabel?: string;
}

/**
 * Two-line "Assigned To" cell. Resolves the agent from the snapshot first,
 * then falls back to `orgUsers[id]`. Renders name (top) + phone (caption).
 * Returns a muted dash when nothing resolves.
 */
export const AssignedAgentCell: FC<AssignedAgentCellProps> = ({
  agent,
  agentId,
  orgUsers,
  emptyLabel = '-',
}) => {
  const resolved = resolveAssignedAgent(agent, agentId, orgUsers ?? []);
  const name = formatAssignedAgentName(resolved) || resolved?.email || null;

  if (!name) return <span className="text-muted-foreground">{emptyLabel}</span>;

  // Name inherits the table's cell font size (set on the <td>) rather than
  // forcing its own, so it lines up with the sibling columns in whatever table
  // renders this cell. Phone stays a muted, proportionally smaller caption.
  return (
    <div>
      <div className="text-foreground">{name}</div>
      {resolved?.phone && (
        <div className="text-[0.85em] leading-tight text-muted-foreground">
          {formatPhoneNumber(resolved.phone)}
        </div>
      )}
    </div>
  );
};
